import { escapeHtmlText } from "./encoding";
import { preprocessAngleBracketHyperlinks } from "./cleaning";

/** Strip trailing punctuation that often hugs URLs in plaintext. */
function trimUrlForDisplay(url: string): string {
  return url.replace(/[.,;:!?'"）\]>]+$/, "");
}

function looksLikeSafeHref(href: string): boolean {
  try {
    const parsed = new URL(href.trim());
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

const URL_IN_PLAIN_PATTERN = /\b(https?:\/\/[^\s<&]+[^\s<&.,;:!?'"）\]]*)/gi;

/** Plaintext/RFC-style angle URL after a phrase — keep visible words, link only the label (e.g. Proofpoint footers). */
const PLAIN_CLICK_HERE_ANGLE_URL_RE =
  /(\bclick\s+)(here)((?:\s+to\s+report\s+this\s+email\s+as\s+spam\.?)?)\s*(?:\n\s*)*<(https?:\/\/[^>\s]+)>/gi;

const PLAIN_THIS_LINK_ANGLE_URL_RE = /(\bthis\s+)(link)\b\s*(?:\n\s*)*<(https?:\/\/[^>\s]+)>/gi;

type PlainEmailChunk =
  | { kind: "plain"; text: string }
  | { kind: "anchor"; href: string; label: string };

function collectPlainPhraseAngleBracketChunks(text: string): PlainEmailChunk[] {
  type Hit = { start: number; end: number; chunks: PlainEmailChunk[] };
  const hits: Hit[] = [];

  let m: RegExpExecArray | null;
  PLAIN_CLICK_HERE_ANGLE_URL_RE.lastIndex = 0;
  while ((m = PLAIN_CLICK_HERE_ANGLE_URL_RE.exec(text)) !== null) {
    const url = trimUrlForDisplay(m[4]);
    if (!looksLikeSafeHref(url)) continue;
    const chunks: PlainEmailChunk[] = [
      { kind: "plain", text: m[1] },
      { kind: "anchor", href: url, label: m[2] },
    ];
    if (m[3]) chunks.push({ kind: "plain", text: m[3] });
    hits.push({ start: m.index, end: m.index + m[0].length, chunks });
  }

  PLAIN_THIS_LINK_ANGLE_URL_RE.lastIndex = 0;
  while ((m = PLAIN_THIS_LINK_ANGLE_URL_RE.exec(text)) !== null) {
    const url = trimUrlForDisplay(m[3]);
    if (!looksLikeSafeHref(url)) continue;
    hits.push({
      start: m.index,
      end: m.index + m[0].length,
      chunks: [
        { kind: "plain", text: m[1] },
        { kind: "anchor", href: url, label: m[2] },
      ],
    });
  }

  hits.sort((a, b) => a.start - b.start);
  const merged: Hit[] = [];
  let fence = -1;
  for (const h of hits) {
    if (h.start >= fence) {
      merged.push(h);
      fence = h.end;
    }
  }

  const out: PlainEmailChunk[] = [];
  let cursor = 0;
  for (const h of merged) {
    if (h.start > cursor) {
      out.push({ kind: "plain", text: text.slice(cursor, h.start) });
    }
    out.push(...h.chunks);
    cursor = h.end;
  }
  if (cursor < text.length) {
    out.push({ kind: "plain", text: text.slice(cursor) });
  }
  return out;
}

function linkifyEscapedPlainFragment(slice: string): string {
  if (!slice) return "";
  const fragments: string[] = [];
  let lastEnd = 0;
  for (const m of slice.matchAll(URL_IN_PLAIN_PATTERN)) {
    const matchStr = m[0];
    const offset = m.index ?? 0;
    if (offset > lastEnd) {
      fragments.push(escapeHtmlText(slice.slice(lastEnd, offset)).replace(/\n/g, "<br>"));
    }
    const hrefCandidate = trimUrlForDisplay(matchStr);
    const displayEscaped = escapeHtmlText(hrefCandidate).replace(/\n/g, "<br>");
    if (looksLikeSafeHref(hrefCandidate)) {
      const hrefAttrEscaped = escapeHtmlText(hrefCandidate);
      fragments.push(
        `<a href="${hrefAttrEscaped}" target="_blank" rel="noopener noreferrer">${displayEscaped}</a>`
      );
    } else {
      fragments.push(escapeHtmlText(matchStr).replace(/\n/g, "<br>"));
    }
    lastEnd = offset + matchStr.length;
  }
  fragments.push(escapeHtmlText(slice.slice(lastEnd)).replace(/\n/g, "<br>"));
  return fragments.join("");
}

/**
 * Builds display HTML string (must still be passed through DOMPurify before dangerouslySetInnerHTML).
 */
export function buildPlaintextEmailDisplayHtml(plain: string): string {
  const normalizedSp = plain.replace(/\u00a0/g, " ");
  if (!normalizedSp) return "";

  const chunks = collectPlainPhraseAngleBracketChunks(normalizedSp);
  const parts: string[] = [];

  for (const ch of chunks) {
    if (ch.kind === "anchor") {
      const hrefEsc = escapeHtmlText(ch.href);
      const labelEsc = escapeHtmlText(ch.label);
      parts.push(`<a href="${hrefEsc}" target="_blank" rel="noopener noreferrer">${labelEsc}</a>`);
      continue;
    }
    const afterAngle = preprocessAngleBracketHyperlinks(ch.text);
    parts.push(linkifyEscapedPlainFragment(afterAngle));
  }

  return parts.join("");
}
